/**
 * Law Firm Account Management Types
 *
 * This module provides comprehensive type definitions for law firm account management,
 * including user management, firm profiles, invitations, and related operations.
 */

import { z } from 'zod';
import type { ThemeChoice } from '@/lib/utils/theme';
import { EMAIL_BODY_FONT_FAMILIES } from '@/lib/constants/email-typography';
import type {
  EmailBodyFontFamily,
  EmailBodyFontSizePt
} from '@/lib/constants/email-typography';

// ============================================================================
// Base Types
// ============================================================================

/**
 * Backend address format (matches law-firms-backend PostalAddressDto)
 */
export interface BackendAddress {
  businessName?: string;
  streetAddress?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  poBox?: string;
  country?: string;
}

/**
 * Frontend address format (for display compatibility)
 */
export interface Address {
  streetAddress: string;
  suburb: string;
  state: string;
  postcode: string;
  country: string;
}

/**
 * Bank account details for trust accounts
 */
export interface BankAccount {
  accountName: string;
  accountNumber: string;
  bsb: string;
}

/**
 * Primary contact information for a law firm
 */
export interface PrimaryContact {
  selectedUserId?: string;
  fullName: string;
  email: string;
  phone: string;
  title?: string;
}

/**
 * Correspondence preferences for communications (frontend display format)
 */
export interface CorrespondencePreferences {
  replyToOption: 'user' | 'firm' | 'generic';
  showUserContact: boolean;
  genericReplyEmail?: string;
}

/**
 * Backend correspondence configuration (matches actual backend DTO)
 */
export interface CorrespondenceConfig {
  defaultSignatory?: 'authenticated_user' | 'solicitor_with_carriage';
  includeSignerNameAfterSignature?: boolean;
  includeSignerTitleAfterSignature?: boolean;
  /**
   * Whether users may override the firm-wide email signature with a personal
   * one. **Default `false`** (VALE-445) — the firm signature plus merge fields
   * is the intended answer, and a personal signature is an exception an admin
   * grants.
   *
   * Enforced on the backend's write path as well as by hiding the editor, so a
   * stale tab cannot save one.
   */
  allowPersonalEmailSignatureOverride?: boolean;
  /**
   * Whether Vale appends an HTML email signature to outbound email at all.
   * **Default `true`** (VALE-490) — so an absent key means enabled, and every
   * read must treat `undefined` as on rather than off.
   *
   * Off suppresses every Vale-side signature, personal as well as firm-wide, for
   * firms whose Outlook integration already signs their mail. It never destroys
   * the stored signature content.
   */
  htmlEmailSignatureEnabled?: boolean;
  outboundCcEmail?: string | null;
  /**
   * Grammatical person of institution-correspondence narrative text: 'we'
   * writes on behalf of the firm, 'individual' on behalf of the signatory.
   */
  firmVoice?: 'we' | 'individual';
  /**
   * How discovery (notification-of-death) letters refer to the estate's legal
   * personal representatives: 'by-name' names them, 'by-role' refers to the
   * role alone ("the Executors"). Default 'by-name' — today's wording.
   *
   * Discovery letters only; redemption letters always name them, because the
   * institution has to identify and verify them from the enclosed
   * identification documents regardless.
   */
  executorReference?: 'by-name' | 'by-role';
  /**
   * **A standing line printed in bold directly above the recipient address on
   * every generated letter** (VALE-1009), e.g. "PRIVATE & CONFIDENTIAL".
   *
   * Empty (the default) prints no line at all, not a blank one, so a firm that
   * never opens the setting sends exactly the letter it sends today.
   *
   * Firm-wide, including for firms on per-office letterhead: the per-office
   * setting chooses which letterhead file prints, while this is part of the
   * body the backend composes.
   *
   * **Clear it with `''`, never `null`.** The backend merges a configuration
   * patch with `deepMerge`, which skips a null source value outright, so a
   * null clear silently leaves the previous line on every letter the firm
   * sends. Bounded by `ADDRESS_PREFIX_LINE_MAX_LENGTH` in
   * `backend/src/law-firms/dto/law-firm-configuration.dto.ts`, which owns the
   * limit.
   */
  addressPrefixLine?: string;
  /**
   * Whether this firm's letters print on a **per-office** letterhead (VALE-540).
   * Default `false` — one firm-wide letterhead, which is what every firm has.
   *
   * Gates resolution as well as the Branding card's list, so switching it off
   * genuinely reverts a firm's output. Nothing is destroyed: an office precedent
   * already uploaded starts printing again the moment it is switched back on.
   */
  perOfficeLetterhead?: boolean;
  /**
   * The font outbound **email bodies** are written in (VALE-574). Absent means
   * Vale's default, Arial 11pt.
   *
   * Email only — a letter's typography belongs to the firm's own letterhead
   * document, so this never affects a generated letter or PDF.
   */
  emailBodyTypography?: {
    fontFamily?: EmailBodyFontFamily;
    fontSizePt?: EmailBodyFontSizePt;
  };
}

/**
 * How much of the distribution a beneficiary declaration may recite.
 *
 * Mirrors the backend's `DECLARATION_AMOUNT_DISCLOSURE_VALUES`. Declared once
 * here as a value so the config interface, the request schema and the settings
 * control all read from it — a second literal is how a picker ends up offering
 * an option the backend rejects.
 */
export const DECLARATION_AMOUNT_DISCLOSURE_VALUES = ['own', 'all'] as const;
export type DeclarationAmountDisclosure =
  (typeof DECLARATION_AMOUNT_DISCLOSURE_VALUES)[number];

/** What a firm gets when it has never opened the setting. */
export const DEFAULT_DECLARATION_AMOUNT_DISCLOSURE: DeclarationAmountDisclosure =
  'own';

/**
 * Law firm configuration object (backend format)
 */
export interface LawFirmConfiguration {
  correspondence?: CorrespondenceConfig;
  notifications?: {
    emailNotificationsEnabled?: boolean;
  };
  tags?: {
    mode?: 'personal' | 'law-firm-wide';
  };
  distributions?: {
    includeBankDetailsInAdvice?: boolean;
    declarationsRequired?: boolean;
    /**
     * How much of the distribution a beneficiary declaration may recite:
     * `'own'` (the default) limits it to the recipient's own share and amount,
     * `'all'` allows the full who-gets-what table. Enforced on the backend when
     * the document is generated, not by how a firm authored its template.
     */
    declarationAmountDisclosure?: DeclarationAmountDisclosure;
    sendModalDefaults?: {
      includeBankVerification?: boolean;
      includeDeclaration?: boolean;
    };
  };
}

// ============================================================================
// Law Firm Types
// ============================================================================

/**
 * File information for letterhead
 */
export interface LetterheadFile {
  id: number;
  originalFilename: string;
  createdAt: string;
  updatedAt: string;
  status: string;
  s3Key: string;
  imageS3Key?: string | null;
  mimeType: string;
  fileType: string;
  extractedData?: Record<string, unknown>;
  isCertifiedCopy: boolean;
  metadata?: Record<string, unknown>;
  analysisMetadata?: Record<string, unknown>;
  fileSizeKb?: number | null;
  deletedAt?: string | null;
}

/**
 * Complete law firm profile information
 */
export interface LawFirmProfile {
  id: number;
  /** The firm's nominated fallback branding office, or null for automatic (VALE-777). */
  defaultOfficeId?: number | null;
  name: string;
  email: string;
  phoneNumber: string;
  fax?: string | null;
  valeEmail: string;
  trustAccountNeedInfo: string;
  verifiedAt: string | null;
  /** `null` clears the address; omitted leaves it untouched (VALE-1505). */
  postalAddress?: BackendAddress | null;
  /** `null` clears the address; omitted leaves it untouched (VALE-1505). */
  businessAddress?: BackendAddress | null;
  trustBankAccount?: BankAccount;
  officeBankAccount?: BankAccount;
  apcaUserId?: string;
  primaryContact?: PrimaryContact;
  primaryContactUser?: {
    id: number;
  };
  correspondencePreferences?: CorrespondencePreferences;
  letterheadFilename?: string;
  /**
   * The **PDF** letterhead file, and nothing else — the artefact the letter
   * editor paints as a background image and the Branding card offers for
   * download.
   *
   * NOT the answer to "does this firm have a letterhead". A firm whose
   * letterhead is a Word precedent has none of these, and gating preview or
   * send on it told such a firm it had no letterhead at all while the backend
   * rendered and sent its letters happily (VALE-865). For that question read
   * `canProduceLetterheadCorrespondence` below.
   */
  letterHeadFileId?: number;
  letterHeadFile?: LetterheadFile;
  /**
   * Whether the firm can produce letterhead correspondence — resolved by the
   * backend, which owns the rule (a PDF letterhead, OR the firm's own
   * institution-letter precedent with the precedent-engine flag on).
   *
   * **This is the gate for anything that previews or sends a letter.** It is
   * the same answer the send validator and the renderer use, so what a firm is
   * offered can never disagree with what the backend will do.
   *
   * Optional because the field post-dates the interface; treat an absent value
   * as `false` (a firm that genuinely cannot send is prompted to fix that,
   * which is the safe direction — the reverse would offer a send the backend
   * refuses).
   */
  canProduceLetterheadCorrespondence?: boolean;
  htmlEmailSignature?: string;
  configuration?: LawFirmConfiguration;
  membershipStatus?:
    | 'pending'
    | 'trial'
    | 'active'
    | 'active-free'
    | 'suspended'
    | 'cancelled'; // Current membership status
  /** Whether the trial-activation gate applies while the firm is `pending`
   *  (VALE-873) — the account attribute that replaced `trial-gate-opt-out`. */
  activationGateRequired?: boolean;
  membershipTier?: 'standard' | 'professional' | 'enterprise'; // Current membership tier
  trialStartedAt?: string | null; // ISO date when trial period started
  trialExpiresAt?: string | null; // ISO date when trial period expires
  membershipActivatedAt?: string | null; // ISO date when membership was activated
  nextBillingDate?: string | null; // ISO date of the next billing boundary (anchored at activation for a new firm)
  scheduledCancellationDate?: string | null; // ISO date when subscription will end if cancellation is pending
  // Invoice / billing details — overrides for what appears on Vale invoices.
  // Null = BE falls back to the corresponding firm-level default.
  billingName?: string | null;
  billingEmail?: string | null;
  billingAbn?: string | null;
  billingAddress?: BackendAddress | null;
  /**
   * Whether the firm has a Stripe customer, resolved by the backend
   * (`AccountService.getProfile`). The Billing tab gates its payment-methods section on
   * it (VALE-428), so it has to survive {@link transformToLawFirm} — it was absent from
   * this interface, which is what let that projection drop it (VALE-1301).
   */
  hasBillingAccount?: boolean;
  faviconUrl?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Request payload for updating law firm profile.
 *
 * **Every key here must also exist on {@link UpdateLawFirmProfileRequestSchema}**, which the
 * type checker enforces — see the reconciliation beside that schema. Adding a field is
 * therefore two edits, and forgetting the second one fails the build instead of silently
 * dropping the value on every save (VALE-1301).
 *
 * The set is `UpdateLawFirmDto`'s accepted fields. `trustAccountNeedInfo`, `primaryContact`
 * and `correspondencePreferences` were removed from it: all three are **response** fields
 * that no caller ever sent, and the backend's `forbidNonWhitelisted` would have answered
 * any of them with a 400 rather than saving it.
 */
export interface UpdateLawFirmProfileRequest {
  /** Nominate the fallback branding office; null clears it (VALE-777). */
  defaultOfficeId?: number | null;
  name?: string;
  email?: string;
  phoneNumber?: string;
  fax?: string | null;
  /** `null` clears the address; omitted leaves it untouched (VALE-1505). */
  postalAddress?: BackendAddress | null;
  /** `null` clears the address; omitted leaves it untouched (VALE-1505). */
  businessAddress?: BackendAddress | null;
  trustBankAccount?: BankAccount;
  officeBankAccount?: BankAccount;
  apcaUserId?: string;
  primaryContactUser?: {
    id: number;
  };
  configuration?: LawFirmConfiguration; // New backend format
}

// ============================================================================
// User Types
// ============================================================================

/**
 * User status within the law firm
 */
export type UserStatus = 'pending' | 'invited' | 'active' | 'deactivated';

/**
 * Structured user preferences to replace generic Record type
 */
export interface MatterViewPreferences {
  tags?: string[];
  types?: string[];
  teamMembers?: string[];
  sortBy?: 'updatedAt' | 'createdAt' | 'lawFirmInternalReference';
  sortOrder?: 'ASC' | 'DESC';
}

export interface UserPreferences {
  /**
   * The user's persisted light/dark/system CHOICE (VALE-548). Written only when
   * the theme toggle is actually used — never on an OS-preference change, which
   * is why it answers "what did people choose" rather than "what did they see".
   *
   * This replaced a dead `ui: { theme, language, timezone }` bag that nothing
   * ever populated or read; the backend's preferences bag holds `theme` at the
   * top level, beside `notifications` and `correspondence`.
   */
  theme?: ThemeChoice;
  correspondence?: {
    forwardCorrespondenceReplies?: boolean;
  };
  notifications?: {
    emailNotificationsEnabled?: boolean;
    smsNotificationsEnabled?: boolean;
    pushNotificationsEnabled?: boolean;
  };
  privacy?: {
    shareDataWithPartners?: boolean;
    allowAnalytics?: boolean;
  };
  matterViews?: {
    all?: MatterViewPreferences;
    archive?: MatterViewPreferences;
  };
}

/**
 * Law firm user profile
 */
export interface LawFirmUser {
  id: number;
  email: string;
  /**
   * All three name columns are **nullable** — `law_firm_users.first_name` /
   * `middle_name` / `last_name` are `nullable: true`, and the backend's own
   * `AccountUserResponseDto` publishes them as `string | null`. A firm user who
   * was invited and has not accepted yet (or one created through the ops
   * portal, which bypasses the invite DTO's `@IsNotEmpty`) has none of them.
   *
   * These were declared non-nullable until LF-6015, which is how the account
   * header came to render the literal string `null null` and to throw
   * `TypeError: Cannot read properties of null (reading 'charAt')` on the
   * initials. Render them through `getUserDisplayName` / `getUserInitials`
   * (`lib/utils/permissions.ts`), which fall back to the non-null `email`.
   */
  firstName: string | null;
  middleName?: string | null;
  lastName: string | null;
  role: string;
  status: UserStatus;
  /**
   * The office this person works out of (VALE-540), or `null` when not recorded
   * — which is a permanently valid state rather than a gap, and what every user
   * is in until their firm both creates offices and assigns them.
   *
   * Feeds the `office.*` email-signature merge fields. It is **not** what decides
   * a letter's letterhead — that is the matter's office, never the signer's.
   */
  officeId?: number | null;
  preference?: UserPreferences;
  handwrittenSignatureFile?: {
    id: number;
    originalFilename: string;
    createdAt: string;
    updatedAt: string;
    status: string;
    s3Key: string;
    imageS3Key?: string | null;
    mimeType: string;
    fileType: string;
    extractedData?: Record<string, unknown>;
    isCertifiedCopy: boolean;
    metadata?: Record<string, unknown>;
    analysisMetadata?: Record<string, unknown>;
    fileSizeKb?: number | null;
    deletedAt?: string | null;
  };
  emailSignature?: string;
  /**
   * Direct phone number. Rendered by the `<<lawyer.phoneNumber>>` merge field in
   * signatures, which is why the team page surfaces it as a fillable column
   * (VALE-445) — before that it existed on the API and nowhere in the product,
   * so no user anywhere had one recorded.
   */
  phoneNumber?: string | null;
  /**
   * Job title / position, as one free-text line including any post-nominals.
   * Rendered by `<<lawyer.title>>`. **Not** {@link role}, which is the internal
   * Vale permission and must never reach a third party.
   */
  title?: string | null;
  /**
   * Their profile page on the firm's own website. The booking confirmation can
   * link their name to it. Null when not recorded.
   */
  profileUrl?: string | null;
  /**
   * Pronouns, as the pronoun string itself and **unbracketed** — `he/him`
   * (VALE-1227). Null when not recorded, which is the default and a permanently
   * valid state.
   *
   * Rendered by `<<signatory.pronouns>>`, which **adds its own brackets**, so
   * nothing on this side should: a user with none must render nothing at all
   * rather than an empty `()`.
   */
  pronouns?: string | null;
  createdAt: string;
  updatedAt: string;
  invitedAt?: string;
  lawFirm?: LawFirmProfile;
  isAdministrator?: boolean;
  hasSignatureAccess?: boolean;
  totpEnabledAt?: string | null;
}

/**
 * Request payload for updating a law firm user
 */
export interface UpdateLawFirmUserRequest {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  /** Empty string clears it. */
  title?: string;
  /** A full `http(s)://` address. Empty string clears it. */
  profileUrl?: string;
  /**
   * Pronouns, unbracketed. Empty string clears it (VALE-1227).
   *
   * Must also be re-declared on {@link UpdateLawFirmUserRequestSchema} — see
   * that schema's own doc for what happens to a key that is not.
   */
  pronouns?: string;
  role?: string;
  status?: UserStatus;
  emailSignature?: string;
  signatureFilename?: string;
  preference?: UserPreferences;
  /**
   * The office this person works out of (VALE-540). `null` clears it — "not
   * recorded" is a permanently valid state, not a gap.
   *
   * Feeds the `office.*` email-signature merge fields. It does **not** decide
   * which letterhead a letter prints on: that comes from the matter's office,
   * never from the signer's.
   */
  officeId?: number | null;
}

// ============================================================================
// Invitation Types
// ============================================================================

/**
 * Individual user invitation data
 */
export interface UserInvitation {
  firstName: string;
  lastName: string;
  email: string;
  /**
   * Optional at invite (VALE-445) — the two facts a firm signature's merge
   * fields need, captured while the admin is already thinking about this person.
   */
  phoneNumber?: string;
  title?: string;
}

/**
 * Request to invite users to the law firm
 */
export interface InviteUsersRequest {
  users: UserInvitation[]; // Array of user objects with names and emails
  role?: string; // Optional default role for all invitees
  message?: string; // Optional personalized message
}

/**
 * Result of a single invitation attempt
 */
export interface InvitationResult {
  email: string;
  success: boolean;
  userId?: number;
  error?: string;
  invitationToken?: string;
}

/**
 * Response from bulk invitation request
 */
export interface InviteUsersResponse {
  results: InvitationResult[];
  successCount: number;
  failureCount: number;
  summary: string;
}

/**
 * Information about a pending invitation
 */
export interface PendingInvitation {
  userId: number;
  email: string;
  invitedAt: string;
  expiresAt: string;
  invitedBy: string;
  status: 'pending' | 'expired' | 'accepted';
}

// ============================================================================
// Action Result Types
// ============================================================================

/**
 * Generic result type for account management operations using discriminated unions for type safety
 */
export type AccountActionResult<T = unknown> =
  | {
      success: true;
      data: T;
      message?: string;
    }
  | {
      success: false;
      error: string;
      validationErrors?: Record<string, string[]>;
      /**
       * The backend refused the write until the user re-authenticates
       * (`STEP_UP_REQUIRED`, VALE-465 / BANKSEC-3) — set when changing a trust
       * account that already has details in it. The caller runs
       * `runStepUpChallenge()` and retries the identical payload; see
       * `lib/auth/step-up.ts`.
       */
      stepUpRequired?: boolean;
      /**
       * The server-signed ticket that must accompany the replay. It carries the
       * moment of refusal and a fingerprint of the pending change, so the
       * re-authentication is bound to *this* submission — a recent login cannot
       * substitute for it. Opaque to the client: carry it back unmodified.
       */
      stepUpTicket?: string;
    };

// ============================================================================
// List and Filter Types
// ============================================================================

/**
 * Filter options for listing law firm users
 */
export interface UserListFilters {
  status?: UserStatus | UserStatus[];
  role?: string;
  search?: string;
  isAdministrator?: boolean;
  hasSignatureAccess?: boolean;
  page?: number;
  limit?: number;
  sortBy?: 'name' | 'email' | 'role' | 'status' | 'createdAt';
  sortOrder?: 'asc' | 'desc';
}

/**
 * Paginated response for user lists
 */
export interface UserListResponse {
  users: LawFirmUser[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// ============================================================================
// Permission Types
// ============================================================================

/**
 * User permissions within the law firm
 */
export interface UserPermissions {
  canManageUsers: boolean;
  canUpdateFirmProfile: boolean;
  canViewAllMatters: boolean;
  canManageTemplates: boolean;
  canViewReports: boolean;
  canManageBilling: boolean;
}

// ============================================================================
// Statistics Types
// ============================================================================

/**
 * Law firm statistics and metrics
 */
export interface LawFirmStatistics {
  totalUsers: number;
  activeUsers: number;
  pendingInvitations: number;
  totalMatters: number;
  activeMatters: number;
  completedMatters: number;
  storageUsed: number; // in bytes
  storageLimit: number; // in bytes
  lastActivityAt: string;
}

// ============================================================================
// Validation Schemas
// ============================================================================

/**
 * Backend address validation schema (for API requests)
 */
export const BackendAddressSchema = z.object({
  businessName: z.string().max(100).optional(),
  streetAddress: z.string().max(100).optional(),
  suburb: z.string().max(50).optional(),
  state: z.string().max(20).optional(),
  postcode: z.string().max(10).optional(),
  poBox: z.string().max(20).optional(),
  country: z.string().max(50).optional()
});

/**
 * Bank account validation schema
 */
export const BankAccountSchema = z.object({
  accountName: z.string().min(1, 'Account name is required').max(100),
  accountNumber: z.string().min(1, 'Account number is required').max(20),
  bsb: z.string().regex(/^\d{3}-?\d{3}$/, 'Invalid BSB format')
});

/**
 * Primary contact validation schema
 */
export const PrimaryContactSchema = z.object({
  selectedUserId: z.string().optional(),
  fullName: z.string().min(1, 'Name is required').max(100),
  email: z.string().email('Invalid email format'),
  phone: z.string().min(1, 'Phone is required').max(20),
  title: z.string().optional()
});

const LawFirmConfigurationSchema = z
  .object({
    correspondence: z
      .object({
        defaultSignatory: z
          .enum(['authenticated_user', 'solicitor_with_carriage'])
          .optional(),
        includeSignerNameAfterSignature: z.boolean().optional(),
        includeSignerTitleAfterSignature: z.boolean().optional(),
        allowPersonalEmailSignatureOverride: z.boolean().optional(),
        htmlEmailSignatureEnabled: z.boolean().optional(),
        outboundCcEmail: z.string().nullable().optional(),
        // Load-bearing, not decorative: UpdateLawFirmProfileRequestSchema
        // derives from this schema and updateLawFirmProfileAction sends the
        // PARSED result, so a key missing here is silently stripped from the
        // PATCH and the setting appears to save while changing nothing.
        firmVoice: z.enum(['we', 'individual']).optional(),
        // Same load-bearing note as firmVoice above: omitted here, the key is
        // stripped from the PATCH and the setting appears to save while every
        // discovery letter keeps naming the executors.
        executorReference: z.enum(['by-name', 'by-role']).optional(),
        // Same load-bearing note as firmVoice above, and the fourth setting on
        // this card to need it: omitted here, the key is stripped from the
        // PATCH, the card's success toast fires, and the line the firm typed
        // never reaches a letter — nor can an existing one be cleared.
        //
        // No `.min(1)`: `''` IS the clear, and the backend's `deepMerge` skips
        // a null source value, so an empty string is the only way to remove a
        // line already stored. `100` mirrors ADDRESS_PREFIX_LINE_MAX_LENGTH in
        // backend/src/law-firms/dto/law-firm-configuration.dto.ts, which owns
        // the limit.
        addressPrefixLine: z.string().max(100).optional(),
        perOfficeLetterhead: z.boolean().optional(),
        // Same load-bearing note as firmVoice above: omitted here, the key is
        // stripped from the PATCH and the font appears to save while every
        // email keeps going out in the old one.
        emailBodyTypography: z
          .object({
            fontFamily: z.enum(EMAIL_BODY_FONT_FAMILIES).optional(),
            fontSizePt: z
              .union([z.literal(10), z.literal(11), z.literal(12)])
              .optional()
          })
          .optional()
      })
      .optional(),
    notifications: z
      .object({
        emailNotificationsEnabled: z.boolean().optional()
      })
      .optional(),
    tags: z
      .object({
        mode: z.enum(['personal', 'law-firm-wide']).optional()
      })
      .optional(),
    distributions: z
      .object({
        includeBankDetailsInAdvice: z.boolean().optional(),
        declarationsRequired: z.boolean().optional(),
        declarationAmountDisclosure: z
          .enum(DECLARATION_AMOUNT_DISCLOSURE_VALUES)
          .optional(),
        sendModalDefaults: z
          .object({
            includeBankVerification: z.boolean().optional(),
            includeDeclaration: z.boolean().optional()
          })
          .optional()
      })
      .optional()
  })
  .optional();

/**
 * Law firm profile validation schema — the **response** shape.
 *
 * It no longer has anything to do with what can be saved: the write contract is declared
 * separately as {@link UpdateLawFirmProfileRequestSchema}, because deriving one from the
 * other is what silently broke four fields (VALE-1301). Several keys below are read-only
 * and would be refused outright by the backend if a caller sent them.
 */
export const LawFirmProfileSchema = z.object({
  id: z.number(),
  name: z.string().min(1).max(100),
  email: z.string().email(),
  phone: z.string().min(1).max(20),
  fax: z.string().max(50).nullable().optional(),
  valeEmail: z.string().email(),
  trustAccountNeedInfo: z.string(),
  verifiedAt: z.string().nullable(),
  /*
    `.nullable()` on both, so a firm can CLEAR an address rather than only edit
    it. The backend stores `null` in each `jsonb` column and its DTO declares
    the null, so stripping it here was the frontend refusing a state the wire
    accepts: the row's `x` built a patch the schema silently dropped, and the
    refetch repainted the old address (VALE-1505).
  */
  postalAddress: BackendAddressSchema.nullable().optional(),
  businessAddress: BackendAddressSchema.nullable().optional(),
  trustBankAccount: BankAccountSchema.optional(),
  primaryContact: PrimaryContactSchema.optional(),
  correspondencePreferences: z
    .object({
      replyToOption: z.enum(['user', 'firm', 'generic']),
      showUserContact: z.boolean(),
      genericReplyEmail: z.string().email().optional()
    })
    .optional(),
  letterheadFilename: z.string().optional(),
  defaultOfficeId: z.number().int().positive().nullable().optional(),
  configuration: LawFirmConfigurationSchema,
  createdAt: z.string(),
  updatedAt: z.string()
});

/**
 * Law firm user validation schema
 */
export const LawFirmUserSchema = z.object({
  id: z.number(),
  email: z.string().email(),
  firstName: z.string().min(1).max(50),
  middleName: z.string().max(50).optional(),
  lastName: z.string().min(1).max(50),
  phoneNumber: z.string().max(30).nullable().optional(),
  title: z.string().max(200).nullable().optional(),
  pronouns: z.string().max(50).nullable().optional(),
  role: z.string().min(1).max(100),
  status: z.enum(['pending', 'invited', 'active', 'deactivated']),
  officeId: z.number().int().positive().nullable().optional(),
  preference: z.record(z.string(), z.unknown()).optional(),
  handwrittenSignatureFile: z
    .object({
      id: z.number(),
      originalFilename: z.string(),
      createdAt: z.string(),
      updatedAt: z.string(),
      status: z.string(),
      s3Key: z.string(),
      imageS3Key: z.string().nullable().optional(),
      mimeType: z.string(),
      fileType: z.string(),
      extractedData: z.record(z.string(), z.unknown()).optional(),
      isCertifiedCopy: z.boolean(),
      metadata: z.record(z.string(), z.unknown()).optional(),
      analysisMetadata: z.record(z.string(), z.unknown()).optional(),
      fileSizeKb: z.number().nullable().optional(),
      deletedAt: z.string().nullable().optional()
    })
    .optional(),
  emailSignature: z.string().max(1000).optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  invitedAt: z.string().optional(),
  lawFirm: LawFirmProfileSchema.optional(),
  isAdministrator: z.boolean().optional(),
  hasSignatureAccess: z.boolean().optional()
});

/**
 * Update law firm profile request validation schema — **the write contract, declared,
 * not derived from the read shape** (VALE-1301).
 *
 * `updateLawFirmProfileAction` PUTs `safeParse`'s OUTPUT, and zod strips an unknown key
 * without an error, so **this key set is the list of fields the firm settings page can
 * actually save**. Anything a caller emits that is absent here is dropped in silence:
 * the request succeeds, the toast fires, and the value never leaves the browser.
 *
 * It used to be `LawFirmProfileSchema.omit({…}).partial()` — the **response** shape with
 * four fields removed. A response shape and a request contract are different sets, so the
 * derivation guaranteed drift in both directions, and both directions had happened:
 *
 * - **Keys the API accepts were missing**, so the write was stripped. `phoneNumber` (the
 *   read schema calls it `phone`), `primaryContactUser`, `apcaUserId` and `officeBankAccount`
 *   were all emitted by the settings page and none of them reached the API. That is the
 *   reported bug: Phone, Primary contact and APCA User ID each reported a successful save
 *   and changed nothing.
 * - **Keys the API rejects were present.** `phone`, `valeEmail` and `letterheadFilename`
 *   are read-only response fields, and the backend runs `forbidNonWhitelisted`
 *   (`backend/src/config/global-validation-pipe.bootstrap.ts`), so a property outside
 *   `UpdateLawFirmDto` is a **400**, not a strip. Nothing emitted them, so this half was
 *   latent rather than live — but it is the same drift, pointing the other way.
 *
 * Declared explicitly, the schema can only be wrong by someone editing it, and
 * {@link UpdateLawFirmProfileRequest} is reconciled against it at compile time below.
 * Every key here is one `UpdateLawFirmDto` accepts.
 */
export const UpdateLawFirmProfileRequestSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  email: z.string().email().optional(),
  /*
    `phoneNumber`, matching the backend DTO and the callers. The read schema's `phone`
    is what the derived version admitted instead, which is why the Phone row saved
    nothing at all.
  */
  phoneNumber: z.string().min(1).max(20).optional(),
  // `.nullable()` is what lets a firm clear the fax: the row sends `null` for empty.
  fax: z.string().max(50).nullable().optional(),
  /*
    `.nullable()` on both, so a firm can CLEAR an address rather than only edit
    it. The backend stores `null` in each `jsonb` column and its DTO declares
    the null, so stripping it here was the frontend refusing a state the wire
    accepts: the row's `x` built a patch the schema silently dropped, and the
    refetch repainted the old address (VALE-1505).
  */
  postalAddress: BackendAddressSchema.nullable().optional(),
  businessAddress: BackendAddressSchema.nullable().optional(),
  trustBankAccount: BankAccountSchema.optional(),
  officeBankAccount: BankAccountSchema.optional(),
  apcaUserId: z.string().optional(),
  primaryContactUser: z.object({ id: z.number().int().positive() }).optional(),
  /*
    VALE-777. `.nullable()` is what lets a firm CLEAR its nomination, which the backend
    distinguishes from an omitted field.
  */
  defaultOfficeId: z.number().int().positive().nullable().optional(),
  configuration: LawFirmConfigurationSchema
});

/**
 * **Compile-time reconciliation, both directions** — the guard that makes the *next*
 * field fail rather than ship silently broken (VALE-1301).
 *
 * The whole defect class here is a key set that drifts out of step with its callers while
 * every test stays green, because a stripped key produces no error anywhere. So the two
 * key sets are asserted equal by the type checker: a field added to
 * {@link UpdateLawFirmProfileRequest} without being added here fails the build naming the
 * key, and a schema key with no place on the request type fails the same way.
 *
 * Both are derived — neither side is a hand-typed list — so this cannot rot the way the
 * five hand-written "load-bearing, don't remove this key" comments it replaces did.
 */
type AssertNever<T extends never> = T;
type _EveryRequestKeyIsAccepted = AssertNever<
  Exclude<
    keyof UpdateLawFirmProfileRequest,
    keyof z.infer<typeof UpdateLawFirmProfileRequestSchema>
  >
>;
type _EverySchemaKeyIsSendable = AssertNever<
  Exclude<
    keyof z.infer<typeof UpdateLawFirmProfileRequestSchema>,
    keyof UpdateLawFirmProfileRequest
  >
>;

/**
 * Update law firm user request validation schema
 */
/**
 * **Every field a caller may PUT must be picked here.** `updateLawFirmUserAction`
 * sends `safeParse`'s *output*, so a key absent from this schema is stripped
 * **without an error** — the request succeeds, the field is silently dropped, and
 * nothing anywhere reports it. That is exactly how the profile's Phone row and
 * the team page's Phone cell both appeared to save and changed nothing.
 *
 * `phoneNumber` and `title` are re-declared rather than picked: the read shape on
 * {@link LawFirmUserSchema} is nullable (the columns are), but the backend DTO
 * takes a `string` and treats empty as a clear — so `null` would be rejected by
 * its `@IsString()`.
 */
export const UpdateLawFirmUserRequestSchema = LawFirmUserSchema.pick({
  firstName: true,
  middleName: true,
  lastName: true,
  email: true,
  role: true,
  status: true,
  emailSignature: true,
  preference: true,
  /*
    Picked deliberately (VALE-540). `updateLawFirmUserAction` sends the
    **zod-parsed** body, so a key absent here is stripped with no error at all —
    the office would appear to save and change nothing. `null` is kept, because
    clearing the assignment is a real instruction the backend honours.
  */
  officeId: true
})
  .partial()
  .extend({
    phoneNumber: z.string().max(30).optional(),
    title: z.string().max(200).optional(),
    // Declared for the reason the doc below gives: a key missing here is
    // stripped by `safeParse`, so the row would appear to save and change nothing.
    profileUrl: z.string().max(2048).optional(),
    /*
      Re-declared for the same reason `title` is: the read shape is nullable
      (the column is), the backend DTO takes a `string` and reads empty as a
      clear, so `null` would be refused by its `@IsString()`. And declared at
      all because the doc above is not decoration — a key missing here is
      stripped by `safeParse` with no error, so the row would appear to save and
      change nothing (VALE-1227).
    */
    pronouns: z.string().max(50).optional()
  });
